import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { MenuItem } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { Popover, PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';

import { KeycloakService } from 'usermgmt';
import { BreadcrumbService } from '../../../core/services/breadcrumb.service';
import { EditorStateService } from '../../../core/services/editor-state.service';
import { StationService } from '../../../core/services/station.service';
import { ThemeMode, ThemeService } from '../../../core/services/theme.service';

@Component({
  selector: 'app-header',
  imports: [
    CommonModule,
    ButtonModule,
    MenuModule,
    BreadcrumbModule,
    AvatarModule,
    TooltipModule,
    PopoverModule,
  ],
  templateUrl: './header.html',
})
export class Header implements OnInit {
  // ===========================================================================
  // 1. Sidebar Toggle
  // ===========================================================================

  @Input() isSidebarOpen = true;
  @Output() toggleSidebar = new EventEmitter<void>();

  // ===========================================================================
  // 2. Breadcrumbs & Navigation
  // ===========================================================================

  protected breadcrumbService = inject(BreadcrumbService);
  protected editorState = inject(EditorStateService);
  private stationService: any = inject(StationService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  menuItems = computed(() => {
    const crumbs = this.breadcrumbService.crumbsSignal();

    return crumbs.map(crumb => ({
      label: crumb,
      command: () => this.onCrumbClick(crumb),
    }));
  });

  onCrumbClick(crumb: string): void {
    if (this.editorState.hasUnsavedChanges()) {
      this.editorState.showSaveWarning.set(true);
      return;
    }
    this.breadcrumbService.emitClick(crumb);

    const text = crumb.trim().toLowerCase();
    if (text === 'home') {
      this.router.navigate(['/dashboard']);
      return;
    } else if (text === 'digital library' || text === 'digital-library') {
      this.router.navigate(['/digital-library']);
      return;
    } else if (text === 'user management') {
      this.router.navigate(['/usermgmt/customer']);
      return;
    } else if (text === 'organizations') {
      this.router.navigate(['/usermgmt/customer']);
      return;
    } else if (text === 'applications') {
      this.router.navigate(['/usermgmt/applications']);
      return;
    } else if (text === 'roles') {
      this.router.navigate(['/usermgmt/roles']);
      return;
    } else if (text === 'users') {
      this.router.navigate(['/usermgmt/users']);
      return;
    }

    const navigateToStation = (stationsList: any[]) => {
      // 1. Exact match by name or code
      let matchedStation = stationsList.find(
        (s: any) => s.name.toLowerCase() === text ||
          s.code.toLowerCase() === text
      );

      // 2. Fallback: substring/includes match (e.g., 'Ongole' matches 'Ongole City')
      if (!matchedStation) {
        matchedStation = stationsList.find(
          (s: any) => s.name.toLowerCase().includes(text) ||
            text.includes(s.name.toLowerCase())
        );
      }

      // 3. Fallback: junction/hub suffix replacement
      if (!matchedStation) {
        matchedStation = stationsList.find(
          (s: any) => s.name.toLowerCase().replace('junction', '').trim() === text ||
            `${s.name} hub`.toLowerCase() === text
        );
      }

      if (matchedStation) {
        this.router.navigate(['/station-data', matchedStation.code], {
          queryParams: { id: matchedStation.id }
        });
      } else {
        console.warn('No matched station found for crumb:', crumb);
      }
    };

    const currentStations = this.stationService.stationsSignal();
    if (currentStations && currentStations.length > 0) {
      navigateToStation(currentStations);
    } else {
      this.stationService.getStations().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (loadedStations: any[]) => {
          navigateToStation(loadedStations);
        },
        error: (err: any) => {
          console.error('Failed to load stations for breadcrumb navigation:', err);
        }
      });
    }
  }

  private checkAndSetUserMgmtCrumbs(url: string): void {
    if (url.includes('/usermgmt/customer/addCustomer')) {
      this.breadcrumbService.setCrumbs(['User Management', 'Organizations', 'Add/Edit Organization']);
    } else if (url.includes('/usermgmt/customer')) {
      this.breadcrumbService.setCrumbs(['User Management', 'Organizations']);
    } else if (url.includes('/usermgmt/applications/assignResource')) {
      this.breadcrumbService.setCrumbs(['User Management', 'Applications', 'Assign Resource']);
    } else if (url.includes('/usermgmt/applications')) {
      this.breadcrumbService.setCrumbs(['User Management', 'Applications']);
    } else if (url.includes('/usermgmt/roles/config')) {
      this.breadcrumbService.setCrumbs(['User Management', 'Roles', 'Configure Role']);
    } else if (url.includes('/usermgmt/roles')) {
      this.breadcrumbService.setCrumbs(['User Management', 'Roles']);
    } else if (url.includes('/usermgmt/users/config')) {
      this.breadcrumbService.setCrumbs(['User Management', 'Users', 'Configure User']);
    } else if (url.includes('/usermgmt/users')) {
      this.breadcrumbService.setCrumbs(['User Management', 'Users']);
    }
  }

  // ===========================================================================
  // 3. Theme Management
  // ===========================================================================

  public themeService = inject(ThemeService);

  @ViewChild('themePopover') themePopover!: Popover;

  themeOptions: { label: string; value: ThemeMode; icon: string }[] = [
    { label: 'System', value: 'system', icon: 'ti ti-device-desktop' },
    { label: 'Light', value: 'light', icon: 'ti ti-sun' },
    { label: 'Dark', value: 'dark', icon: 'ti ti-moon' },
  ];

  /**
   * Returns icon corresponding to the currently selected theme mode
   */
  get currentThemeIcon(): string {
    const mode = this.themeService.mode();
    switch (mode) {
      case 'dark':
        return 'ti ti-moon';
      case 'light':
        return 'ti ti-sun';
      default:
        return 'ti ti-device-desktop';
    }
  }

  /**
   * Toggles theme selection popover
   */
  popoverClick(event: Event): void {
    this.themePopover.toggle(event);
  }

  /**
   * Applies selected theme and closes theme popover
   */
  setTheme(value: ThemeMode, popover: Popover): void {
    this.themeService.setMode(value);
    popover.hide();
  }

  // ===========================================================================
  // 4. User Profile & Account Menu
  // ===========================================================================

  private keycloakService = inject(KeycloakService);

  hasImage = false;
  profileImage = 'assets/images/profile.jpg';

  firstName = 'Super';
  lastName = 'Admin';

  get initials(): string {
    return `${this.firstName.charAt(0)}${this.lastName.charAt(0)}`.toUpperCase();
  }

  items: MenuItem[] = [
    {
      label: 'Profile',
      icon: 'ti ti-user',
      disabled: true,
      command: () => {},
    },
    {
      label: 'Settings',
      icon: 'ti ti-settings',
      disabled: true,
      command: () => {},
    },
    {
      label: 'Logout',
      icon: 'ti ti-logout',
      command: () => {
        this.keycloakService.logout();
      },
    },
  ];

  // ===========================================================================
  // 5. Lifecycle Hooks
  // ===========================================================================

  constructor() {
    this.checkAndSetUserMgmtCrumbs(this.router.url);
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event: NavigationEnd) => {
      this.checkAndSetUserMgmtCrumbs(event.urlAfterRedirects);
    });
  }

  ngOnInit(): void {}
}
